import React from 'react';
import PhoneIcon from '../Icons/PhoneIcon';
import SecondaryCtaLink from '../SecondaryCtaLink/SecondaryCtaLink';
import PrimaryCtaLink from '../PrimaryCtaLink/PrimaryCtaLink';
import TextLink from '../TextLink/TextLink';
import MenuLink from '../MenuLink/MenuLink';
import { ContentfulLinkProps } from './types';
import CallRailReadyGate from '../CallRail/CallRailReadyGate';
import { buttonBase, buttonSizes, buttonVariants } from '../Button/buttonStyles';
import { addTrailingSlashToInternalUrl } from '@/lib/utils/trailingSlash';

/**
 * Helper function to ensure target="_blank" links always have rel="noopener noreferrer"
 */
function getSecureRel(targetAttribute?: string, relAttribute?: string): string | undefined {
  if (targetAttribute === '_blank') {
    const existingRels = relAttribute ? relAttribute.split(' ') : [];
    const securityRels = ['noopener', 'noreferrer'];
    const mergedRels = [...new Set([...existingRels, ...securityRels])];
    return mergedRels.join(' ');
  }
  return relAttribute;
}

/**
 * Renders the CallRail phone CTA in a context-specific style.
 * Always renders an anchor to the resolved `tel:` href — no booking logic.
 */
function renderPhoneNode(data: ContentfulLinkProps): React.ReactElement | null {
  const { resolvedPhoneNumber, resolvedTelHref, context, phoneColorClassName } = data;

  if (!resolvedPhoneNumber || !resolvedTelHref) return null;

  const isDarkBackground = phoneColorClassName?.includes('text-white');
  const defaultPhoneButtonVariant = isDarkBackground
    ? buttonVariants.heroSecondary
    : buttonVariants.secondary;

  if (context === 'sticky-mobile-footer') {
    return (
      <a
        href={resolvedTelHref}
        aria-label={`Call ${resolvedPhoneNumber}`}
        className='bg-sticky-footer-call-bg rounded-full flex items-center border-4 border-white p-3 top-0'
      >
        <PhoneIcon className='h-14 w-14 text-sticky-footer-call-icon' />
      </a>
    );
  }

  if (context === 'footer' || context === 'layout-highlighted') {
    return (
      <>
        {/* Mobile: "Call Now" Button */}
        <a
          href={resolvedTelHref}
          className={`${buttonBase} ${buttonSizes.full} ${buttonVariants.footerMobile} flex w-full flex-1 sm:hidden `}
        >
          <PhoneIcon className='text-white w-5 h-5 fill-current' />
          <span className=''>Call Now</span>
        </a>
        {/* Desktop: Dynamic Phone Number Label */}
        <a
          href={resolvedTelHref}
          className='hidden sm:flex items-center gap-2 h-10 hover:opacity-80 transition-opacity duration-200 text-white justify-center sm:justify-end'
        >
          <PhoneIcon className='text-brand-icon w-5 h-5 fill-current' />
          <span className='heading-4 text-white'>{resolvedPhoneNumber}</span>
        </a>
      </>
    );
  }

  if (context === 'mobile-row') {
    return (
      <a
        href={resolvedTelHref}
        className={`${buttonBase} ${buttonSizes.compact} ${buttonVariants.navPrimary} flex-1 font-bold`}
      >
        <PhoneIcon className='text-mobile-menu-footer-icon-text w-5 h-5 fill-current' />
        Call Now
      </a>
    );
  }

  if (context === 'hero-banner' || context === 'hero-location') {
    return (
      <a
        href={resolvedTelHref}
        className={`${buttonBase} ${buttonSizes.default} ${buttonVariants.heroSecondary} bg-transparent whitespace-nowrap`}
      >
        <PhoneIcon className='text-image-banner-secondary-btn-icon w-5 h-5' />
        {resolvedPhoneNumber}
      </a>
    );
  }

  if (context === 'desktop-navigation') {
    return (
      <a
        href={resolvedTelHref}
        className='group inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-accent px-6 py-4 transition-colors duration-200 hover:opacity-90 md:w-auto md:justify-start md:rounded-none md:bg-transparent md:px-0 md:py-0 md:hover:bg-transparent md:hover:opacity-80'
      >
        <PhoneIcon className='h-4 w-4 fill-current text-white md:text-brand-icon' />
        <span className=' md:hidden'>Call Now</span>
        <span className={`hidden md:inline heading-6 ${phoneColorClassName || 'text-primary-500'}`}>
          {resolvedPhoneNumber}
        </span>
      </a>
    );
  }

  return (
    <a
      href={resolvedTelHref}
      className={`${buttonBase} ${buttonSizes.default} ${defaultPhoneButtonVariant} group w-fit`}
    >
      <PhoneIcon className='h-4 w-4 fill-current text-brand-icon transition-colors duration-200 group-hover:text-white' />
      <span className='md:hidden'>Call Now</span>
      <span
        className={`hidden md:inline transition-colors duration-200 ${
          phoneColorClassName || 'text-primary-500 group-hover:text-white'
        }`}
      >
        {resolvedPhoneNumber}
      </span>
    </a>
  );
}

export default function ContentfulLink({
  data,
  children,
}: {
  data: ContentfulLinkProps;
  children?: React.ReactNode;
}) {
  const { priority, url } = data;
  const secureRel = getSecureRel(data.targetAttribute, data.relAttribute);

  // ─── CALLRAIL VARIANT (Phone CTA) ───
  if (priority === 'CallRail') {
    return <CallRailReadyGate>{renderPhoneNode(data)}</CallRailReadyGate>;
  }

  // For non-CallRail links, if no URL is provided, do not render anything.
  if (!url) return null;

  const normalizedUrl = addTrailingSlashToInternalUrl(url);

  // ─── PRIMARY VARIANT (Booking CTA) ───
  if (priority === 'Primary') {
    const buttonText = data.title || data.label || 'Book Now';
    const layout = data.context && data.context !== 'menu' ? data.context : 'default';

    return (
      <PrimaryCtaLink
        buttonText={buttonText}
        linkUrl={normalizedUrl}
        target={data.targetAttribute}
        relAttribute={secureRel}
        className={data.className}
        layout={layout}
      >
        {children}
      </PrimaryCtaLink>
    );
  }

  // ─── SECONDARY VARIANT (Ghost/Outline Button) ───
  if (priority === 'Secondary') {
    const urlPathWithoutQuery = url.split('?')[0].replace(/\/+$/, '');
    const fallbackLabel = `Learn more about ${urlPathWithoutQuery.split('/').pop()}`;
    const buttonText = data.title || data.label || fallbackLabel;

    // On hero banners the background is a dark image — secondary-500 brand colors
    // are invisible. Use the hero variant: brand-accent border/fill, white text.
    if (data.context === 'hero-banner') {
      return (
        <SecondaryCtaLink
          buttonText={buttonText}
          linkUrl={normalizedUrl}
          target={data.targetAttribute}
          relAttribute={secureRel}
          className={data.className}
          variant='hero'
        >
          {children}
        </SecondaryCtaLink>
      );
    }

    return (
      <SecondaryCtaLink
        buttonText={buttonText}
        linkUrl={normalizedUrl}
        target={data.targetAttribute}
        relAttribute={secureRel}
        className={data.className}
      >
        {children}
      </SecondaryCtaLink>
    );
  }

  // ─── MENU LINK VARIANT ───
  if (data.context === 'menu') {
    return (
      <MenuLink
        href={normalizedUrl}
        target={data.targetAttribute}
        rel={secureRel}
        className={data.className}
      >
        {children || data.title || data.label || 'Menu Item'}
      </MenuLink>
    );
  }

  // ─── DEFAULT VARIANT (TextLink / Inline) ───
  return (
    <TextLink
      href={normalizedUrl}
      target={data.targetAttribute}
      rel={secureRel}
      text={data.title || data.label || 'Read more'}
      className={data.className}
      size='lg'
    >
      {children}
    </TextLink>
  );
}
